import { MONITORING_FILE_DOWNLOAD_ERROR_MESSAGE } from '@/shared/modules/monitoring/entities/monitoring.const';
import { MonitoringService } from '@/shared/modules/monitoring/services/monitoringService';
import { RequestError } from '@/shared/services/httpClient.interface';
import { downloadFileLink } from '@/utils/common';
import { dispatchToast } from '@/utils/tryOrCatchMessageError';
import { useCallback, useState } from 'react';

const monitoringService = new MonitoringService();

const getDownloadErrorMessage = (error: unknown): string => {
  const status = (error as RequestError)?.response?.status;

  if (status === 403) {
    return MONITORING_FILE_DOWNLOAD_ERROR_MESSAGE.FORBIDDEN;
  }

  if (status === 404) {
    return MONITORING_FILE_DOWNLOAD_ERROR_MESSAGE.NOT_FOUND;
  }

  return MONITORING_FILE_DOWNLOAD_ERROR_MESSAGE.DEFAULT;
};

/**
 * Hook compartilhado para baixar o arquivo original de um File do monitoring
 * via `GET /files/:id/download` (usado por movimentação e posição cadastral).
 *
 * - Trata a resposta como binária (Blob) e dispara o download no navegador.
 * - Extrai o nome do arquivo do header `Content-Disposition`, com fallback para
 *   o `fallbackFileName` informado (o front já conhece `name`/`extension` do File).
 * - Mapeia os erros 403/404 para mensagens amigáveis; o 401 é tratado globalmente
 *   pelo interceptor de auth (modal-login).
 */
const useMonitoringFileDownload = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const downloadFile = useCallback(
    async (fileId?: string, fallbackFileName?: string): Promise<boolean> => {
      if (!fileId) {
        return false;
      }

      setLoading(true);

      try {
        const { blob, fileName } = await monitoringService.downloadFile(fileId, fallbackFileName);

        const blobUrl = URL.createObjectURL(blob);
        downloadFileLink(blobUrl, fileName ?? fallbackFileName ?? `arquivo-${fileId}`);
        URL.revokeObjectURL(blobUrl);

        return true;
      } catch (error) {
        console.error(error);

        dispatchToast({ text: getDownloadErrorMessage(error) });

        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    downloadFile,
  };
};

export default useMonitoringFileDownload;
